/** 셀 연동(동일값) popover 프레젠테이션 컴포넌트 */

import * as Popover from "@radix-ui/react-popover";
import CellLinkageFloatingPanel, {
  type CellLinkageItem,
  type CellNotice,
  type ModifiedCellDisplayValues,
} from "../cell-linkage-floating-panel";

/**
 * 셀 선택 시 우측에 띄우는 floating panel 상태. 세 가지 모드:
 * - `sum-validation`: autoSumValueDifferent 셀 — "입력한 값/계산 결과" 행 + 연동 위치
 * - `linked-cells`: identity 가 연동된 셀 — 위치 리스트만. 보기 모드·편집 모드
 *   (IXD-DSD 대사·IXD 에디터) 공통으로 노출한다.
 * - `modified`: 연동은 없지만 사용자가 직접 수정한 셀 — 기존 값/수정한 값만.
 *
 * `element` 는 Radix Popover virtualRef anchor 로 사용된다.
 */
export type CellPopoverState =
  | {
      mode: "sum-validation";
      element: HTMLElement;
      inputValue: string;
      calculatedValue: string;
      identityKey: string | null;
    }
  | {
      mode: "linked-cells";
      element: HTMLElement;
      identityKey: string;
    }
  | {
      mode: "modified";
      element: HTMLElement;
    }
  /** 연동도 수정도 없지만 값이 들어가지 않아 알릴 것이 있는 셀 */
  | {
      mode: "cell-notice";
      element: HTMLElement;
      identityKey: string;
    };

interface CellLinkagePopoverProps {
  cellPopover: CellPopoverState | null;
  anchorRef: { current: HTMLElement | null };
  items: CellLinkageItem[];
  /** 선택 셀이 수정된 경우 기존/수정 표시값 — 패널 하단에 노출 */
  modifiedValues?: ModifiedCellDisplayValues | null;
  /** 값이 들어가지 않은 이유와 선택지 — 패널 최상단에 노출 */
  notice?: CellNotice | null;
  onClose: () => void;
}

export const CellLinkagePopover = ({
  cellPopover,
  anchorRef,
  items,
  modifiedValues,
  notice,
  onClose,
}: CellLinkagePopoverProps) => {
  return (
    <Popover.Root
      open={!!cellPopover}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Popover.Anchor virtualRef={anchorRef} />
      <Popover.Portal>
        <Popover.Content
          side="right"
          align="center"
          sideOffset={2}
          collisionPadding={16}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          // anchor 셀 자체를 다시 클릭하면 outside 로 잡혀 popover 가 닫히고,
          // 셀 selection 은 그대로라 onSelectionChange 가 다시 발화하지
          // 않아 panel 이 영구히 닫혀 보이는 문제를 막는다.
          onPointerDownOutside={(event) => {
            const target = event.target as Node | null;
            if (target && cellPopover?.element.contains(target)) {
              event.preventDefault();
            }
          }}
          // Portal 로 렌더돼 컨테이너 외부에 있으면 useTableCellSelection 의
          // document-level mousedown 핸들러가 외부 클릭으로 오인식해 선택을
          // clear → cellPopover null → popover unmount → onClick 발화 못함.
          // 네이티브 이벤트 자체를 중단해 document 핸들러까지 도달 못 하게 한다.
          onMouseDownCapture={(event) => event.nativeEvent.stopPropagation()}
          className="z-50"
        >
          {cellPopover && (
            <CellLinkageFloatingPanel
              // 다른 셀로 이동 시 패널 내부 상태(단위 변환 토글)를 리셋
              key={cellPopover.element.id}
              mode={cellPopover.mode}
              inputValue={
                cellPopover.mode === "sum-validation"
                  ? cellPopover.inputValue
                  : undefined
              }
              calculatedValue={
                cellPopover.mode === "sum-validation"
                  ? cellPopover.calculatedValue
                  : undefined
              }
              modifiedValues={modifiedValues}
              notice={notice}
              items={items}
              onClose={onClose}
            />
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
